/**
 * IOT-HAL-1: MockVendorAdapter — in-memory DeviceContract implementation.
 *
 * Used for tests, demos, and Storybook. Simulates the complete device lifecycle:
 *   register → telemetry stream → config push → applied → event emit
 *   → health update → decommission
 *
 * Every state transition emits the correct EventBus event when running under
 * HalService (HalService wraps all lifecycle and config calls with EventBus).
 * The adapter itself does not import EventBusService — that's HalService's job.
 *
 * Foundation Purity: no tenant-specific logic here.
 */

import { createId } from '../../utils/createId.js';
import { normalizeTimestamp } from '../../normalization/normalizeTimestamp.js';
import { canonicalUnit } from '../../normalization/normalizeUnits.js';
import { isCanonicalEventType } from '../../normalization/normalizeEventType.js';

/** Default capability matrix for the mock vendor — supports everything. */
const MOCK_CAPABILITIES = {
  configKeys: {
    reporting_interval_sec: {
      pushable: true,
      readable: true,
      range: [1, 3600],
      requiresActivation: false,
      unit: 'seconds',
      description: 'Mock reporting interval',
    },
    temp_alert_max_c: {
      pushable: true,
      readable: true,
      range: [-40, 100],
      requiresActivation: false,
      unit: '°C',
      description: 'Mock temperature alert max',
    },
    firmware_version: {
      pushable: true,
      readable: true,
      requiresActivation: false,
      description: 'Mock firmware version',
    },
    serial_number: {
      pushable: false,
      readable: true,
      requiresActivation: true,
      description: 'Mock serial — activation-only',
    },
  },
  telemetryTypes: [
    'temperature_c',
    'humidity_pct',
    'gps_position',
    'battery_soc_pct',
    'cell_signal_dbm',
  ],
  alertingMode: 'cloud',
  otaSupported: true,
  attestationSupported: false,
  rollbackSupported: true,
  pushLatencyExpectedMs: 0, // synchronous in mock
};

export class MockVendorAdapter {
  /**
   * @param {import('../../contracts/DeviceContract.ts').DeviceRecord} device
   * @param {object} [opts]
   * @param {import('../../contracts/DeviceContract.ts').CapabilityMatrix} [opts.capabilities]
   */
  constructor(device, opts = {}) {
    this._device          = device;
    this._capabilities    = opts.capabilities || MOCK_CAPABILITIES;
    this._lifecycleState  = device.lifecycleState || 'provisioned';

    /** @type {Record<string, unknown>} */
    this._config = {
      reporting_interval_sec: 60,
      temp_alert_max_c: 8,
      firmware_version: '1.0.0',
      serial_number: device.serialNumber || 'MOCK-SN-001',
    };
    this._configVersion  = 1;
    this._pendingConfig  = null;

    /** @type {import('../../contracts/DeviceContract.ts').NormalizedReading[]} */
    this._telemetryLog = [];

    /** @type {import('../../contracts/DeviceContract.ts').DeviceEvent[]} */
    this._eventLog = [];

    this._telemetrySubscribers = [];
    this._eventSubscribers     = [];

    this._health = {
      deviceId:                  device.id,
      lastSeenAt:                new Date().toISOString(),
      batterySocPct:             85,
      batteryVoltageV:           3.7,
      cellSignalDbm:             -75,
      firmwareVersion:           '1.0.0',
      firmwareUpdateAvailable:   false,
      lastSelfTestPassed:        true,
      connectivityStatus:        'online',
      estimatedBatteryDaysRemaining: 30,
    };
  }

  // ── Identity ────────────────────────────────────────────────────────────────

  getDeviceId()     { return this._device.id; }
  getVendor()       { return 'mock'; }
  getModel()        { return this._device.model || 'MockDevice-v1'; }
  getCapabilities() { return this._capabilities; }

  // ── Configuration ───────────────────────────────────────────────────────────

  async pushConfig(delta, actor) {
    const receiptId = createId('rcpt');
    const pushedAt  = new Date().toISOString();

    const failedKeys = [];
    const applied    = {};

    for (const [key, value] of Object.entries(delta.changes)) {
      const cap = this._capabilities.configKeys[key];
      if (!cap || !cap.pushable) {
        failedKeys.push({ key, reason: 'not pushable' });
        continue;
      }
      applied[key] = value;
    }

    if (failedKeys.length > 0) {
      return {
        receiptId,
        deviceId:         this._device.id,
        status:           'partially_applied',
        pushedAt,
        appliedAt:        new Date().toISOString(),
        failedKeys,
        rollbackAvailable: true,
      };
    }

    // Synchronous apply for mock
    Object.assign(this._config, applied);
    this._configVersion += 1;
    this._pendingConfig = null;

    return {
      receiptId,
      deviceId:         this._device.id,
      status:           'applied',
      pushedAt,
      appliedAt:        new Date().toISOString(),
      rollbackAvailable: true,
    };
  }

  async getCurrentConfig() {
    return {
      schemaVersion: 1,
      tenantId:      this._device.tenantId,
      configValues:  { ...this._config },
      configVersion: this._configVersion,
      appliedAt:     new Date().toISOString(),
    };
  }

  async getPendingConfig() {
    if (!this._pendingConfig) return null;
    return {
      schemaVersion: 1,
      tenantId:      this._device.tenantId,
      configValues:  { ...this._pendingConfig },
      configVersion: this._configVersion,
      appliedAt:     new Date().toISOString(),
    };
  }

  async rollbackConfig(toVersion, _actor) {
    const receiptId = createId('rcpt');
    // Mock rollback: reset to a known baseline if version matches
    if (toVersion <= this._configVersion) {
      this._configVersion = toVersion;
      return {
        receiptId,
        deviceId:         this._device.id,
        status:           'applied',
        pushedAt:         new Date().toISOString(),
        appliedAt:        new Date().toISOString(),
        rollbackAvailable: true,
      };
    }
    return {
      receiptId,
      deviceId:         this._device.id,
      status:           'failed',
      pushedAt:         new Date().toISOString(),
      failedKeys: [{ key: '*', reason: `version ${toVersion} not available` }],
      rollbackAvailable: false,
    };
  }

  // ── Telemetry ────────────────────────────────────────────────────────────────

  async getTelemetry(window, sensorTypes) {
    let readings = this._telemetryLog;
    if (window) {
      const from = new Date(window.from).getTime();
      const to   = new Date(window.to).getTime();
      readings = readings.filter((r) => {
        const ts = new Date(r.timestamp).getTime();
        return ts >= from && ts <= to;
      });
    }
    if (sensorTypes?.length) {
      readings = readings.filter((r) => sensorTypes.includes(r.sensorType));
    }
    return readings;
  }

  subscribeToTelemetry(handler) {
    this._telemetrySubscribers.push(handler);
    return {
      unsubscribe: () => {
        this._telemetrySubscribers = this._telemetrySubscribers.filter((h) => h !== handler);
      },
    };
  }

  // ── Health ──────────────────────────────────────────────────────────────────

  async getDeviceHealth() {
    return { ...this._health };
  }

  // ── Events ──────────────────────────────────────────────────────────────────

  subscribeToEvents(handler) {
    this._eventSubscribers.push(handler);
    return {
      unsubscribe: () => {
        this._eventSubscribers = this._eventSubscribers.filter((h) => h !== handler);
      },
    };
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  getLifecycleState() { return this._lifecycleState; }

  async setLifecycleState(state, _actor) {
    this._lifecycleState = state;
  }

  // ── Test helpers (not part of DeviceContract) ─────────────────────────────

  /**
   * Inject a simulated telemetry reading directly into the subscriber stream.
   * Used in tests to simulate inbound telemetry without a real device.
   *
   * @param {Partial<import('../../contracts/DeviceContract.ts').NormalizedReading>} partial
   */
  simulateReading(partial) {
    const reading = {
      deviceId:    this._device.id,
      tenantId:    this._device.tenantId,
      sensorType:  partial.sensorType || 'temperature_c',
      value:       partial.value ?? 5.0,
      unit:        partial.unit || canonicalUnit(partial.sensorType || 'temperature_c'),
      timestamp:   partial.timestamp || new Date().toISOString(),
      vendor:      'mock',
      qualityFlag: partial.qualityFlag || 'ok',
    };
    this._telemetryLog.push(reading);
    this._telemetrySubscribers.forEach((h) => h(reading));
    return reading;
  }

  /**
   * Inject a simulated device event into the subscriber stream.
   *
   * @param {string} eventType  must be a canonical event type
   * @param {object} [payload]
   * @param {'info' | 'warning' | 'critical'} [severity]
   */
  simulateEvent(eventType, payload = {}, severity = 'info') {
    if (!isCanonicalEventType(eventType)) {
      throw new Error(`simulateEvent: "${eventType}" is not a canonical event type`);
    }
    const event = {
      eventId:   createId('evt'),
      deviceId:  this._device.id,
      tenantId:  this._device.tenantId,
      eventType,
      severity,
      payload,
      timestamp: new Date().toISOString(),
      vendor:    'mock',
    };
    this._eventLog.push(event);
    this._eventSubscribers.forEach((h) => h(event));
    return event;
  }

  /**
   * Update mock health state for testing health degradation/restoration flows.
   *
   * @param {Partial<import('../../contracts/DeviceContract.ts').DeviceHealth>} patch
   */
  patchHealth(patch) {
    Object.assign(this._health, patch, { deviceId: this._device.id });
  }

  /** Return all telemetry readings in the log. */
  getTelemetryLog() { return [...this._telemetryLog]; }

  /** Return all device events in the log. */
  getEventLog() { return [...this._eventLog]; }
}

// ── Factory ───────────────────────────────────────────────────────────────────

/**
 * Factory for HalService.registerAdapter('mock', MockVendorAdapterFactory).
 *
 * @param {object} [opts]
 * @param {import('../../contracts/DeviceContract.ts').CapabilityMatrix} [opts.capabilities]
 * @returns {import('../../contracts/DeviceContract.ts').AdapterFactory}
 */
export function createMockAdapterFactory(opts = {}) {
  return (device) => new MockVendorAdapter(device, opts);
}

/**
 * Parse a raw MockVendor webhook payload into a NormalizedReading array.
 * Used by iotIngest to test inbound normalization without real hardware.
 *
 * Mock webhook format:
 *   { vendorMessageId, deviceId, readings: [{ type, v, ts, unit? }] }
 *
 * @param {string} deviceId
 * @param {string} tenantId
 * @param {object} rawPayload
 * @returns {import('../../contracts/DeviceContract.ts').NormalizedReading[]}
 */
export function parseMockVendorInbound(deviceId, tenantId, rawPayload) {
  if (!rawPayload?.readings) return [];
  return rawPayload.readings.map((r) => {
    const ts = normalizeTimestamp(r.ts || r.timestamp);
    return {
      deviceId,
      tenantId,
      sensorType:      r.type,
      value:           r.v ?? r.value,
      unit:            r.unit || canonicalUnit(r.type),
      timestamp:       ts || new Date().toISOString(),
      vendor:          'mock',
      vendorRawPayload: r,
      qualityFlag:     ts ? 'ok' : 'invalid',
    };
  });
}
